import "@/components/history/history.css";
import { historyWords as words, historyText as text } from "@/components/history/history-copy";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router";
import { Bento } from "@/components/ui/box";
import { useClassroomImport } from "@/components/imports/use-classroom-import";
import { useRunImportBox } from "@/components/imports/use-run-import-box";
import { useRegistrarRosterBox } from "@/components/imports/use-registrar-roster-box";
import { historyBox, historyRead } from "@/components/history/history-boxes";
import { importsApi, type ImportBatch } from "@/lib/api/imports";
import { PROTECTED_ROUTES } from "@/navigation/routes";
import { searchWithTrail } from "@/lib/visit/address";
import { RegistrarLocalNavigation } from "@/components/registrar/RegistrarLocalNavigation";

export function ImportsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathId = useParams()["*"]?.split("/").filter(Boolean)[0];
  const legacyId = new URLSearchParams(location.search).get("batch");
  const classroom = useClassroomImport();
  const query = useQuery({ queryKey: ["import-batches"], queryFn: importsApi.getBatches });
  const ranking = useQuery({ queryKey: ["imports-page-ranking"], queryFn: importsApi.ranking });
  const rank = (id: string) => ranking.data?.state === "ready" ? ranking.data.value.candidates.find((c) => c.id === id)?.rank ?? null : null;
  const courseImport = useRunImportBox(classroom.prepared, { keepOpen: classroom.keepOpen, parts: classroom.parts, batches: classroom.batchPart, blockers: classroom.blockers, rank: rank("history-import-course") });
  const rosterImport = useRegistrarRosterBox(rank("history-import-roster"));
  // Keep shared paths and older batch/receipt links pointing at their record.
  useEffect(() => {
    const runId = pathId || legacyId;
    if (!runId) return;
    const next = new URLSearchParams(location.search);
    next.delete("batch");
    navigate({ pathname: PROTECTED_ROUTES.admin.historicalImports,
      search: searchWithTrail(next.toString(), [`run:${runId}`]) }, { replace: true });
  }, [pathId, legacyId, navigate, location.search]);
  const runs = historyBox({ kind: "list", id: "import-runs", job: words.findAnImportRunAndReadItsReceipts, label: words.importRuns,
    owner: { system: "imam" }, read: historyRead(query), fold: "never",
    detail: (v: ImportBatch[]) => text.recordedRuns(v.length),
    state: (v: ImportBatch[]) => ({ class: "quiet", word: v.length ? words.runsRecorded : words.noImportRunsYet }),
    figures: (v: ImportBatch[]) => [
      { value: v.length, unit: words.runs }, { value: v.filter((r) => r.mode === "apply").length, unit: words.applied },
      { value: v.filter((r) => r.mode === "dry_run").length, unit: words.dryRuns },
      { value: v.reduce((n, r) => n + r.rejected_count, 0), unit: words.refusedRows },
    ],
    rows: (v: ImportBatch[]) => v,
    row: { key: (r) => r.id, title: (r) => r.source_name,
      facts: (r) => text.refusedRows2(r.mode === "dry_run" ? "Dry run" : "Apply", r.status, r.rejected_count),
      act: (r) => ({ verb: words.readRun2, does: "visit", visit: `run:${r.id}` }) },
  });
  return <div className="app-page">
    <RegistrarLocalNavigation area="history" />
    <Bento id="historical-imports" journey="Historical Imports" boxes={[...classroom.boxes, ...(classroom.available ? [courseImport] : []), rosterImport, runs]} defaultOpen={[runs.id]} />
  </div>;
}
